import { Injectable, effect, inject, EventEmitter, Injector, runInInjectionContext } from '@angular/core';
import { BaseDataService, OfflineModeService, StorageService, LocalStorageService, TransformationService, MigrationService } from '@luzula/luzula-lib';
import { AufnahmePunktDocument } from './data-model';
import { ToastrService } from 'ngx-toastr';
import { ProjectService } from './project.service';
import { DatabaseConfigService } from './database-config.service';
import { seedMigrationsDocument } from './migrations';
import { setBodenartenTabelle } from './bohrpunkte/evaluators/nwsk';
import { ListenService, BodenartEntry } from './listenverwaltung/listen.service';

// Re-export SyncProgress from BaseDataService
export type { SyncProgress } from '@luzula/luzula-lib';

// Filter-Funktionen für benutzerdefinierte Listen (werden von Komponenten verwendet)
export function hauptbodenformFilter(type: string, i = 0) {
	return (value: string, row: any) => {
		// if (idOf(type) === 7 && i === 0) return value.endsWith('M');
		return true;
	};
}

export function feinbodenformFilter(hbf: string) {
	return (value: string, row: any) => {
		if (!hbf) return true;
		if (!row || !row['HBF']) return value === hbf;
		return row['HBF'].trim() === hbf;
	};
}

export function wuchsbezirkFilter(wg: string) {
	return (value: string, row: any) => {
		if (!wg) return true;
		if (!row || !row['WG']) return value === wg;
		return row['WG'].trim() === wg;
	};
}

// Editierbare Listen - werden mit benutzerdefinierten Werten überschrieben
export const EDITABLE_LISTS = [
	'Feinbodenformen',
	'Forstämter', 'Humusformen', 'HorizontSymbole', 'Bodenarten', 'BodenartenTabelle', 'Wuchsgebiete',
	'ReliefformUmgebung', 'Nährkraftstufen', 'Substrattypengruppen',
	'BodenformSuffixe', 'Torfarten', 'Farben', 'Klimastufen'
];
export const STORAGE_PREFIX = 'userList_';

/**
 * DataService für Bohrpunkte-Verwaltung.
 *
 * Erbt alle generische Funktionalität von BaseDataService:
 * - CRUD: get, getAll, put, bulkGet, bulkDocs, remove, removeBulk
 * - Query: queryIndex, getDocument$, saveDocument
 * - Download: downloadDocuments, downloadDocumentsByIds, ensureEmptyOfflineDatabase
 * - Sync: syncToRemote, syncCurrentProjectToRemote, checkinBohrpunkte, exitOfflineMode
 * - Views: storeViews, getView, getFilter
 * - HardData: getHardcoded, loadCustomLists, reloadCustomList
 * - Offline: discardDownloadedPoints, arePointsDownloaded, etc.
 *
 * App-spezifische Logik ist in BohrpunktDocumentHandler implementiert.
 */
@Injectable({
	providedIn: 'root'
})
export class DataService extends BaseDataService<AufnahmePunktDocument> {
	private injector = inject(Injector);
	private dbConfig = inject(DatabaseConfigService);
	private _storage = inject(StorageService);
	private _oms = inject(OfflineModeService);
	private _ps = inject(ProjectService);
	private _msg = inject(ToastrService);
	private _ts = inject(TransformationService);
	private ls = inject(LocalStorageService);
	private migrationService = inject(MigrationService);

	// Service-Getter für BaseDataService
	protected override get storage() { return this._storage; }
	protected override get oms() { return this._oms; }
	protected override get ps() { return this._ps; }
	protected override get msg() { return this._msg; }
	protected override get ts() { return this._ts; }

	// ========== BasePouchDbService Abstract Implementations ==========

	override get remoteDbName(): string {
		return this.dbConfig.punkteDbName;
	}

	override get localDbName(): string {
		// Lokale DB nutzt Projekt-ID als Namen
		return this.ps.project() || '';
	}

	protected override get projectSignal() {
		return this.ps.project;
	}

	protected override get userOfflineModeSignal() {
		return this.oms.userOfflineMode;
	}

	protected override get networkOnlineSignal() {
		return this.oms.networkOnline;
	}

	protected override async onInitializeRemoteDatabase(db: PouchDB.Database): Promise<void> {
		await Promise.all([
			this.ensureApplicationDesignDocument(db, true),
			seedMigrationsDocument(db)
		]);
		await this.migrationService.initialize();
	}

	protected override async onInitializeLocalDatabase(db: PouchDB.Database): Promise<void> {
		await this.ensureApplicationDesignDocument(db);
		// Migrationen auch im Offline-Modus laden (initialize() hat Guard gegen Doppelaufruf)
		await this.migrationService.initialize();
	}

	protected override onDatabasesInitialized(): void {
		this.isInitialized = true;
		this.initialized.emit();
		// currentDb_ nur emittieren wenn eine echte DB verfügbar ist
		if (this.db()) {
			this.currentDb_.next(this.db()!);
		}
	}

	protected override onDatabasesClosed(): void {
		// DataService hat keine spezielle Logik beim Schließen
	}

	protected override async shouldKeepEmptyLocalDb(dbName: string): Promise<boolean> {
		const wasDownloaded = await this.storage.getProjectValue('pointsDownloaded', dbName);
		return this.oms.userOfflineMode() || wasDownloaded === true;
	}

	protected override onConnectionFailed(): void {
		// Events werden von BaseDataService verwaltet
		// connectionFailed.emit() wird in BaseDataService aufgerufen
	}

	protected override onConnectionSuccess(message?: string): void {
		// Events werden von BaseDataService verwaltet
		// connected.emit() wird in BaseDataService aufgerufen
	}

	// ========== End BasePouchDbService Implementations ==========

	constructor() {
		super();

		// MigrationService mit DB-Getter verbinden
		this.migrationService.setRemoteDbGetter(() => this.remoteDb ?? null);

		// Lade benutzerdefinierte Listen aus LocalStorage
		this.loadCustomListsFromLocalStorage();

		// WICHTIG: Warte auf ProjectService.initialized, damit das Projekt aus Storage geladen wurde.
		// Sonst initialisiert sich DS zweimal (ohne/mit Projekt) → doppeltes Laden, Karten-Flicker.
		this._ps.initialized.subscribe(() => {
			runInInjectionContext(this.injector, () => this._initDatabases());
		});

		// App-spezifischer Effect: _documentsDownloaded Status bei Projektwechsel aktualisieren
		effect(async () => {
			const projectId = this.ps.project();

			// Bei Projektwechsel: Status zurücksetzen
			this._documentsDownloaded = false;

			// Wenn lokale DB verfügbar ist: Status wiederherstellen
			if (this.localDb && projectId) {
				try {
					const info = await this.localDb.info();
					if (info.doc_count > 0) {
						this._documentsDownloaded = true;
					} else {
						const wasDownloaded = await this.storage.getProjectValue('pointsDownloaded', projectId);
						if (wasDownloaded === true) {
							this._documentsDownloaded = true;
						}
					}
				} catch (err) {
					console.warn('[DataService] Fehler beim Prüfen von _documentsDownloaded:', err);
				}
			}
		});
	}

	// ========== Wrapper-Methoden für Rückwärtskompatibilität ==========

	// Events (zusätzlich zu denen aus BaseDataService)
	nummerChanged = new EventEmitter<{ id: string, num: string}>();

	// hardData für direkten Zugriff (Komponenten nutzen das)
	get hardData(): any {
		const map = this.documentHandler.getHardData();
		const obj: any = {};
		map.forEach((value, key) => {
			obj[key] = value;
		});
		return obj;
	}

	// Wrapper: updateBohrpunkt für Komponenten (ruft transformAfterLoad auf)
	updateBohrpunkt(bp: any, fetchWmsData = true): any {
		bp = this.documentHandler.transformAfterLoad(bp);
		if (fetchWmsData) {
			this.documentHandler.enrichDocument(bp);
		}
		return bp;
	}

	// Wrapper: removeBohrpunkt → remove
	async removeBohrpunkt(id: string) {
		return this.remove(id);
	}

	// Wrapper: removeBohrpunkteBulk → removeBulk
	async removeBohrpunkteBulk(ids: string[]) {
		return this.removeBulk(ids);
	}

	// Wrapper: checkoutBohrpunkte → downloadDocuments
	async checkoutBohrpunkte(bnds?: any[], filterName?: string|null): Promise<void> {
		return this.downloadDocuments(bnds, filterName);
	}

	// Wrapper: checkoutEmpty → ensureEmptyOfflineDatabase
	async checkoutEmpty(): Promise<void> {
		return this.ensureEmptyOfflineDatabase();
	}

	// Wrapper: addBohrpunkte → bulkDocs
	async addBohrpunkte(bpDocs: any[]) {
		return this.bulkDocs(bpDocs);
	}

	// Wrapper: getBohrpunkteBatch → bulkGet
	async getBohrpunkteBatch(ids: string[]): Promise<any[]> {
		return this.bulkGet(ids);
	}

	// Wrapper: createBohrpunktFrom (DEPRECATED, aber für Übergangsphase)
	async createBohrpunktFrom(bp: AufnahmePunktDocument, options?: { keepHorizonte?: boolean, keepSubstrate?: boolean, keepVegetationen?: boolean }) {
		const newDoc = this.documentHandler.createNew(bp);
		if (!options?.keepHorizonte) newDoc.Horizontfolge = [];
		if (!options?.keepSubstrate) newDoc.Substratfolge = [];
		if (!options?.keepVegetationen) newDoc.bodenvegetation = { aufnahme: { schichtAnteile: {}, vegetationen: [] } };
		newDoc.header.bemerkungen = '';
		delete (newDoc as any)._id;
		delete (newDoc as any)._rev;

		const result = await this.db()?.post(newDoc);
		if (result) {
			(newDoc as any)._id = result.id;
			(newDoc as any)._rev = result.rev;
			this.pointSaved.emit(newDoc);
			if (this.oms.userOfflineMode()) {
				this.hasPendingChanges.set(true);
			}
		}
		return result;
	}

	// Override discardLocal als public
	override async discardLocal() {
		await super.discardLocal();
	}

	// ========== App-Spezifische Hilfsmethoden ==========

	/**
	 * Lädt benutzerdefinierte Listen aus LocalStorage.
	 * Erweitert die Basis-loadCustomLists() Methode.
	 */
	private async loadCustomListsFromLocalStorage() {
		for (const listName of EDITABLE_LISTS) {
			const customList = await this.ls.get(STORAGE_PREFIX + listName);
			if (customList) {
				this.customListCache.set(listName, customList as any[]);
			}
		}
		this.applyCustomBodenartenTabelle();
	}

	/**
	 * Lädt eine einzelne benutzerdefinierte Liste neu aus LocalStorage.
	 * Wird nach dem Speichern in der Listenverwaltung aufgerufen.
	 */
	public override async reloadCustomList(listName: string) {
		const customList = await this.ls.get(STORAGE_PREFIX + listName);
		if (customList) {
			this.customListCache.set(listName, customList as any[]);
		} else {
			// Falls gelöscht wurde, aus Cache entfernen
			this.customListCache.delete(listName);
		}
		if (listName === 'BodenartenTabelle') {
			this.applyCustomBodenartenTabelle();
		}
	}

	/** Setzt benutzerdefinierte BodenartenTabelle für die nWSK-Berechnung. */
	private applyCustomBodenartenTabelle() {
		const custom = this.customListCache.get('BodenartenTabelle') as BodenartEntry[] | undefined;
		if (custom?.length) {
			setBodenartenTabelle(ListenService.bodenartenTabelleFromArray(custom));
		}
	}

	/**
	 * Utility: Mapped einen Wert aus einer HardData-Tabelle anhand der ID.
	 * DEPRECATED: Sollte über getHardcoded() verwendet werden.
	 */
	public mapFromHardData(which: string, id: string): string | undefined {
		const table = this.getHardcoded(which);
		if (!table) return undefined;

		for (const data of table) {
			if (data.ID !== id) continue;
			for (const k of Object.keys(data)) {
				if (k !== 'ID') return data[k];
			}
		}

		return undefined;
	}
}
